import React, { useMemo, useState } from "react"
import { ArrowLeft, ArrowRight, Check, Copy, Mail, RotateCcw, Sparkles, FileText, ExternalLink, Lock, Cloud, HelpCircle, UserCheck } from "lucide-react"
import { SectionHeading } from "./ui/SectionHeading"
import {
	INTENTS,
	IntentId,
	DataPolicy,
	HumanApproval,
	InquirySelection,
	EMPTY_SELECTION,
	buildInquiryResult,
	buildInquirySummary,
	buildInquiryMailto
} from "../utils/inquiry/engine"
import { PRODUCT_BY_ID, INQUIRY_EMAIL, OFFER_PDF_URL } from "../data/products"

type Step = 1 | 2 | 3

const STEP_LABELS: Record<Step, string> = { 1: "Záměr", 2: "Upřesnění", 3: "Doporučení" }

const DATA_POLICY_OPTIONS: { id: DataPolicy; label: string; hint: string; icon: React.ReactNode }[] = [
	{ id: "onprem", label: "Data musí zůstat u nás", hint: "on-premise, lokální AI modely", icon: <Lock size={16} /> },
	{ id: "cloud", label: "Cloud je v pořádku", hint: "Claude, Gemini, Groq, Make.com", icon: <Cloud size={16} /> },
	{ id: "unsure", label: "Zatím nevíme", hint: "rozhodneme v analýze", icon: <HelpCircle size={16} /> }
]

const HUMAN_APPROVAL_OPTIONS: { id: HumanApproval; label: string; hint: string }[] = [
	{ id: "yes", label: "Ano, člověk schvaluje", hint: "Human-in-the-Loop" },
	{ id: "no", label: "Ne, plně automaticky", hint: "deterministický provoz" },
	{ id: "unsure", label: "Zatím nevíme", hint: "poradíme" }
]

/**
 * InquiryWizard Component — deterministic "inquiry assistant": the visitor picks what they need,
 * answers two constraints and gets recommended solutions, products, documents and a pre-filled
 * e-mail to the inquiry mailbox. No backend, no AI calls — rules live in utils/inquiry/engine.ts.
 */
export const InquiryWizard: React.FC = () => {
	const [step, setStep] = useState<Step>(1)
	const [selection, setSelection] = useState<InquirySelection>(EMPTY_SELECTION)
	const [stepError, setStepError] = useState<string>("")
	const [copied, setCopied] = useState<boolean>(false)

	const result = useMemo(() => buildInquiryResult(selection), [selection])
	const summary = useMemo(() => buildInquirySummary(selection, result), [selection, result])
	const mailto = useMemo(() => buildInquiryMailto(selection, result), [selection, result])

	const toggleIntent = (id: IntentId) => {
		setStepError("")
		setSelection(prev => ({
			...prev,
			intents: prev.intents.includes(id) ? prev.intents.filter(i => i !== id) : [...prev.intents, id]
		}))
	}

	const goNext = () => {
		if (step === 1 && selection.intents.length === 0) {
			setStepError("Vyberte prosím alespoň jeden záměr.")
			return
		}
		setStepError("")
		setStep(prev => (prev < 3 ? ((prev + 1) as Step) : prev))
	}

	const goBack = () => {
		setStepError("")
		setStep(prev => (prev > 1 ? ((prev - 1) as Step) : prev))
	}

	const reset = () => {
		setSelection(EMPTY_SELECTION)
		setStep(1)
		setStepError("")
		setCopied(false)
	}

	const copySummary = async () => {
		try {
			await navigator.clipboard.writeText(summary)
			setCopied(true)
			window.setTimeout(() => setCopied(false), 2500)
		} catch (error) {
			console.warn("Clipboard write failed", error)
			setCopied(false)
		}
	}

	return (
		<section id="inquiry" className="lp-section lp-section--dark">
			<div className="lp-container">
				<SectionHeading
					eyebrow="Automat poptávky"
					title="Sestavte si poptávku za dvě minuty"
					lead={
						<>
							Vyberte, co potřebujete vyřešit, a automat vám deterministicky – bez jakékoliv AI – doporučí vhodné
							řešení z našeho ekosystému, odkáže na podklady a připraví e-mail s poptávkou na{" "}
							<a href={`mailto:${INQUIRY_EMAIL}`}>{INQUIRY_EMAIL}</a>.
						</>
					}
				/>

				<div className="lp-wizard" data-reveal="">
					{/* Step indicator */}
					<ol className="lp-wizard__steps" aria-label="Kroky poptávky">
						{([1, 2, 3] as Step[]).map(s => (
							<li
								key={s}
								className={`lp-wizard__step${step === s ? " is-active" : ""}${step > s ? " is-done" : ""}`}
								aria-current={step === s ? "step" : undefined}
							>
								<span className="lp-wizard__step-num" aria-hidden="true">{step > s ? <Check size={14} strokeWidth={3} /> : s}</span>
								<span>{STEP_LABELS[s]}</span>
							</li>
						))}
					</ol>

					{/* Step 1: intents */}
					{step === 1 && (
						<div className="lp-wizard__panel">
							<h3>Co potřebujete vyřešit?</h3>
							<p className="lp-wizard__hint">Můžete vybrat více možností.</p>

							<div className="lp-wizard__intents" role="group" aria-label="Záměry">
								{INTENTS.map(intent => {
									const checked = selection.intents.includes(intent.id)
									return (
										<button
											key={intent.id}
											type="button"
											role="checkbox"
											aria-checked={checked}
											className={`lp-intent${checked ? " is-checked" : ""}`}
											onClick={() => toggleIntent(intent.id)}
										>
											<span className="lp-intent__check" aria-hidden="true"><Check size={14} strokeWidth={3} /></span>
											<span className="lp-intent__title">{intent.title}</span>
											<span className="lp-intent__text">{intent.text}</span>
											<span className="lp-intent__examples">
												{intent.examples.map(ex => <span key={ex}>{ex}</span>)}
											</span>
										</button>
									)
								})}
							</div>
						</div>
					)}

					{/* Step 2: constraints */}
					{step === 2 && (
						<div className="lp-wizard__panel">
							<h3>Dvě otázky, které ovlivní návrh</h3>

							<div className="lp-wizard__question">
								<span className="lp-wizard__label">Kde mohou být zpracována vaše data?</span>
								<div className="lp-wizard__choices" role="radiogroup" aria-label="Umístění dat">
									{DATA_POLICY_OPTIONS.map(opt => (
										<button
											key={opt.id}
											type="button"
											role="radio"
											aria-checked={selection.dataPolicy === opt.id}
											className={`lp-choice${selection.dataPolicy === opt.id ? " is-checked" : ""}`}
											onClick={() => setSelection(prev => ({ ...prev, dataPolicy: opt.id }))}
										>
											<span className="lp-choice__icon" aria-hidden="true">{opt.icon}</span>
											<span className="lp-choice__label">{opt.label}</span>
											<span className="lp-choice__hint">{opt.hint}</span>
										</button>
									))}
								</div>
							</div>

							<div className="lp-wizard__question">
								<span className="lp-wizard__label">Musí výstupy před použitím schválit člověk?</span>
								<div className="lp-wizard__choices" role="radiogroup" aria-label="Lidské schválení">
									{HUMAN_APPROVAL_OPTIONS.map(opt => (
										<button
											key={opt.id}
											type="button"
											role="radio"
											aria-checked={selection.humanApproval === opt.id}
											className={`lp-choice${selection.humanApproval === opt.id ? " is-checked" : ""}`}
											onClick={() => setSelection(prev => ({ ...prev, humanApproval: opt.id }))}
										>
											<span className="lp-choice__icon" aria-hidden="true"><UserCheck size={16} /></span>
											<span className="lp-choice__label">{opt.label}</span>
											<span className="lp-choice__hint">{opt.hint}</span>
										</button>
									))}
								</div>
							</div>

							<label className="lp-field">
								<span className="lp-wizard__label">Co vás dnes nejvíc brzdí? (nepovinné)</span>
								<textarea
									className="lp-textarea lp-input--dark"
									rows={4}
									maxLength={600}
									placeholder="Např.: Objednávky z e-shopu přepisujeme ručně do účetnictví, výkresy od dodavatelů procházíme ručně…"
									value={selection.note}
									onChange={e => setSelection(prev => ({ ...prev, note: e.target.value }))}
								/>
							</label>
						</div>
					)}

					{/* Step 3: result */}
					{step === 3 && (
						<div className="lp-wizard__panel">
							<h3><Sparkles size={20} /> Doporučené řešení</h3>
							<p className="lp-wizard__hint">
								Sestaveno pravidlovým automatem z vašich odpovědí. Přesný návrh architektury a cenu dostanete po úvodní konzultaci zdarma.
							</p>

							<div className="lp-wizard__results">
								{result.recommendations.map(rec => (
									<article key={rec.intent} className="lp-reco">
										<h4>{rec.title}</h4>
										<p>{rec.text}</p>
										{rec.products.length > 0 && (
											<div className="lp-reco__products">
												{rec.products.map(id => (
													<a key={id} href="#products" className="lp-chip lp-chip--dark">{PRODUCT_BY_ID[id].name}</a>
												))}
											</div>
										)}
										<div className="lp-reco__links">
											{rec.links.map(link => (
												<a
													key={link.href + link.label}
													href={link.href}
													target={link.external ? "_blank" : undefined}
													rel={link.external ? "noopener noreferrer" : undefined}
												>
													{link.external ? <FileText size={14} /> : <ExternalLink size={14} />} {link.label}
												</a>
											))}
										</div>
									</article>
								))}

								{result.ai && (
									<article className="lp-reco lp-reco--ai">
										<h4>{result.ai.title}</h4>
										<p>{result.ai.text}</p>
									</article>
								)}

								{result.humanApproval && (
									<article className="lp-reco lp-reco--ai">
										<h4>Lidské schválení výstupů</h4>
										<p>{result.humanApproval}</p>
									</article>
								)}
							</div>

							<div className="lp-wizard__summary">
								<span className="lp-wizard__label">Souhrn poptávky (text e-mailu)</span>
								<textarea className="lp-textarea lp-input--dark" rows={9} readOnly value={summary} aria-label="Souhrn poptávky" />
								<p className="lp-wizard__hint">
									Kliknutím na tlačítko se otevře váš e-mailový klient s předvyplněnou poptávkou na{" "}
									<a href={`mailto:${INQUIRY_EMAIL}`}>{INQUIRY_EMAIL}</a>. Doplňte prosím název firmy a telefon – ozveme se do 24 hodin.
								</p>
								<div className="lp-wizard__actions">
									<a href={mailto} className="lp-btn lp-btn--primary">
										<Mail size={18} /> Napsat e-mail s poptávkou
									</a>
									<button type="button" className="lp-btn lp-btn--ghost" onClick={copySummary}>
										{copied ? <Check size={18} /> : <Copy size={18} />} {copied ? "Zkopírováno" : "Kopírovat souhrn"}
									</button>
									<a href={OFFER_PDF_URL} target="_blank" rel="noopener noreferrer" className="lp-btn lp-btn--ghost">
										<FileText size={18} /> Nabídka řešení (PDF)
									</a>
								</div>
							</div>
						</div>
					)}

					{stepError && <p className="lp-wizard__error" role="alert">{stepError}</p>}

					{/* Navigation */}
					<div className="lp-wizard__nav">
						{step > 1 ? (
							<button type="button" className="lp-btn lp-btn--ghost" onClick={goBack}>
								<ArrowLeft size={18} /> Zpět
							</button>
						) : <span />}

						{step < 3 ? (
							<button type="button" className="lp-btn lp-btn--primary" onClick={goNext}>
								{step === 1 ? "Pokračovat" : "Zobrazit doporučení"} <ArrowRight size={18} />
							</button>
						) : (
							<button type="button" className="lp-btn lp-btn--ghost" onClick={reset}>
								<RotateCcw size={18} /> Začít znovu
							</button>
						)}
					</div>
				</div>
			</div>
		</section>
	)
}
